import { formatSeconds2HMS } from "@/utils/formatter";
import IcRecordStart from "@/assets/svg/ic_record_start.svg?react";
import IcRecordPause from "@/assets/svg/ic_record_pause.svg?react";
import IcRecordFinish from "@/assets/svg/ic_record_finish.svg?react";
import Tooltip from "@/component/Tooltip";
import IcClearScreen from "@/assets/svg/ic_clear_screen.svg?react";
import deviceStore from "@/store/device.store";
import { shallow } from "zustand/shallow";
import { useState, useRef, useEffect } from "react";
import { message } from "antd";
import lottie, { AnimationItem } from "lottie-web";
import recording from "@/assets/simulInter/recording.json";
interface RecordControllerProps {
  pauseRecording?: () => Promise<any>;
  resumeRecording?: () => Promise<any>;
  onClearScreen?: () => void;
  onFinish?: () => void;
}

function RecordController(props: RecordControllerProps) {
  const { pauseRecording, resumeRecording, onClearScreen, onFinish } = props;
  const [pauseLoading, setPauseLoading] = useState(false);

  // Lottie 动画相关
  const lottieContainer = useRef<HTMLDivElement>(null);
  const animationInstance = useRef<AnimationItem | null>(null);

  // 从 deviceStore 读取录制状态（使用 shallow 比较确保 duration 和 isPaused 变化时都能更新）
  const { recordTime, isPaused, wsConnected } = deviceStore(
    (state) => ({
      recordTime: state.recordingStatus?.duration ?? 0,
      isPaused: state.recordingStatus?.isPaused ?? false,
      wsConnected: state.wsConnected,
    }),
    shallow
  );

  // 初始化 Lottie 动画
  useEffect(() => {
    if (lottieContainer.current && !animationInstance.current) {
      animationInstance.current = lottie.loadAnimation({
        container: lottieContainer.current,
        renderer: "svg",
        loop: true,
        autoplay: !isPaused,
        animationData: recording,
      });
    }

    // 组件卸载时销毁动画
    return () => {
      if (animationInstance.current) {
        animationInstance.current.destroy();
        animationInstance.current = null;
      }
    };
  }, []);

  // 根据 isPaused 状态控制动画播放/暂停
  useEffect(() => {
    if (animationInstance.current) {
      if (isPaused || !wsConnected) {
        animationInstance.current.pause();
      } else {
        animationInstance.current.play();
      }
    }
  }, [isPaused, wsConnected]);

  const handlePauseToggle = async () => {
    if (!wsConnected) {
      message.error("设备连接已断开，无法使用该功能");
      return;
    }

    if (pauseLoading) {
      return;
    }

    setPauseLoading(true);
    try {
      if (isPaused) {
        await resumeRecording?.();
      } else {
        await pauseRecording?.();
      }
      message.warning(isPaused ? "大屏已继续录制" : "大屏已暂停录制");
    } catch (error) {
      console.error("暂停/恢复录制失败:", error);
      message.error(isPaused ? "恢复录制失败" : "暂停录制失败");
    } finally {
      setPauseLoading(false);
    }
  };

  const handleClearScreen = () => {
    onClearScreen?.();
  };

  const handleFinish = () => {
    if (!wsConnected) {
      message.error("设备连接已断开，无法使用该功能");
      return;
    }
    onFinish?.();
  };

  return (
    <div className="w-full h-16 rounded-br-2 rounded-bl-2 flex items-center justify-between px-6">
      {/* 左侧：波形动画 + 时间 */}
      <div className="flex items-center">
        <div ref={lottieContainer} className="w-10 h-8" />
        <div className="text-3.5 text-mx-secondary text-3.5 lh-5.5 ml-2">
          {formatSeconds2HMS(recordTime)}
        </div>
      </div>

      {/* 右侧：控制按钮 */}
      <div className="flex items-center gap-4">
        {/* 清屏按钮 */}
        <div className="cursor-pointer flex items-center" onClick={handleClearScreen}>
          <Tooltip content="一键清除大屏字幕">
            <IcClearScreen className="w-10 h-10" />
          </Tooltip>
        </div>

        {/* 暂停/播放按钮 */}
        <div
          className={`flex items-center ${pauseLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
          onClick={handlePauseToggle}
        >
          <Tooltip content={pauseLoading ? "处理中..." : (isPaused ? "继续" : "暂停")}>
            {isPaused ? (
              <IcRecordStart className="w-10 h-10" />
            ) : (
              <IcRecordPause className="w-10 h-10" />
            )}
          </Tooltip>
        </div>

        {/* 完成按钮 */}
        <div className="cursor-pointer flex items-center" onClick={handleFinish}>
          <Tooltip content="完成录制">
            <IcRecordFinish className="w-10 h-10" />
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

export default RecordController;
